import React, { useEffect, useState } from "react";
import { motion, useMotionValue } from "framer-motion";
import { useRef } from "react";

export default function PhotoCarousel({ images }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const dragX = useMotionValue(0);
  const [dragging, setDragging] = React.useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const containerRef = useRef(null);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
        // Set height based on aspect ratio (16:9) for mobile
        setContainerHeight(width * (window.innerWidth < 768 ? 9 / 16 : 0.6));
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, [containerRef.current]);

  const nextImage = () => {
    if (currentIndex < images.length - 1) {
      setDirection(1);
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const prevImage = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const onDragStart = () => {
    setDragging(true);
  };

  const onDragEnd = (_, info) => {
    setDragging(false);
    const threshold = containerWidth * 0.2; // 20% of container width

    if (Math.abs(info.offset.x) > threshold) {
      if (info.offset.x < 0 && currentIndex < images.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else if (info.offset.x > 0 && currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      }
    }

    dragX.set(0);
  };

  return (
    <div className="relative w-full">
      <motion.div
        className="overflow-hidden rounded-xl relative"
        ref={containerRef}
        style={{ height: containerHeight }}
      >
        <motion.div
          className="flex items-center cursor-grab active:cursor-grabbing"
          drag="x"
          dragConstraints={{
            left: -containerWidth * (images.length - 1),
            right: 0,
          }}
          dragElastic={0.1}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          animate={{
            x: -currentIndex * containerWidth,
          }}
          transition={{
            type: "spring",
            damping: 30,
            stiffness: 200,
          }}
        >
          {images.map((photo, index) => (
            <div
              key={index}
              className="shrink-0"
              style={{
                width: containerWidth,
                height: containerHeight,
              }}
            >
              <img
                src={photo}
                alt={`property-${index + 1}`}
                className="w-full h-full object-cover rounded-xl"
                draggable="false"
              />
            </div>
          ))}
        </motion.div>

        {/* Navigation arrows - only show on desktop */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full backdrop-blur-sm transition-all"
              style={{ opacity: currentIndex === 0 ? 0.5 : 1 }}
              disabled={currentIndex === 0}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={nextImage}
              className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full backdrop-blur-sm transition-all"
              style={{ opacity: currentIndex === images.length - 1 ? 0.5 : 1 }}
              disabled={currentIndex === images.length - 1}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}
      </motion.div>

      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/20 backdrop-blur-sm">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-1.5 h-1.5 rounded-full transition-all ${
                index === currentIndex ? "bg-white w-3" : "bg-white/50"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Mobile swipe indicator - only show on first image */}
      {currentIndex === 0 && images.length > 1 && (
        <div className="md:hidden absolute bottom-12 left-1/2 -translate-x-1/2 text-white text-xs bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm whitespace-nowrap">
          Swipe to view more
        </div>
      )}
    </div>
  );
}